// PostHog Event Catalog — единый source of truth для product-аналитики.
//
// Zod-схема каждого события. Импортируется server (apps/server/.../analytics.ts)
// + client (apps/web/.../analytics.ts) → type-safe `track(event, props)`.
//
// PII (§14.9 ТЗ): НЕ кладём userId/materialId как high-cardinality
// aggregation-label; идентификаторы (setId/quizId) допустимы как reference,
// но не для группировок. email/phone/IP — НИКОГДА не в properties.
//
// См. docs/phases/PHASE-6-OBSERVABILITY.md §5.7.
import { z } from "zod";

export const events = {
	// ─── Auth & Onboarding ─────────────────────────────────────────────
	signup_started: z.object({ provider: z.literal("sso-exchange") }),
	signup_completed: z.object({
		country: z.string(),
		educationLevel: z.string().optional(),
		referralSource: z.string().optional(),
	}),
	user_signed_in: z.object({ provider: z.string() }),
	user_signed_out: z.object({}),
	consent_accepted: z.object({ documents: z.array(z.string()) }),
	onboarding_step_completed: z.object({
		stepName: z.string(),
		stepDurationSec: z.number(),
	}),
	/**
	 * Шаг онбординга пропущен (PRODUCT-NARRATIVE §5.3). Отдельное событие, а не
	 * `onboarding_step_completed` с флагом: воронка «дошёл / пропустил / отвалился»
	 * различает пропуск и прохождение, и склеивать их в одно имя — терять её.
	 * `stepName` — тот же словарь, что у события выше.
	 */
	onboarding_step_skipped: z.object({
		stepName: z.string(),
		stepDurationSec: z.number(),
	}),
	onboarding_template_selected: z.object({
		templateId: z.string(),
		templateSubject: z.string(),
	}),
	onboarding_completed: z.object({ totalDurationSec: z.number() }),
	first_material_uploaded: z.object({ materialType: z.string() }),
	account_deletion_requested: z.object({}),
	/**
	 * Открыта справка «Как это работает» (PRODUCT-NARRATIVE §7). Постоянный вход,
	 * а не разовый экран онбординга, поэтому `source` — откуда позвали
	 * (`onboarding` / `section-intro` / `top-bar` / `empty-state`), а `tab` — на
	 * какой вкладке открылась модалка.
	 */
	how_it_works_opened: z.object({
		source: z.string(),
		tab: z.string().optional(),
	}),
	/**
	 * Свёрнут баннер-объяснение раздела (PRODUCT-NARRATIVE §8.1).
	 * `moduleId` — `ModuleId` из реестра модулей (низкая кардинальность,
	 * группировать по нему безопасно).
	 *
	 * Эмитится СЕРВЕРОМ (`onboarding.dismissIntro`) — на клиенте не дублировать.
	 */
	section_intro_dismissed: z.object({ moduleId: z.string() }),
	/**
	 * Пункт чек-листа первых шагов выполнен (PRODUCT-NARRATIVE §5.5).
	 * `itemKey` — ключ из `onboardingChecklistSchema` (`hasMaterial`, `hasCourse`,
	 * `hasCompletedTopic`, `hasTutorSession`, `hasRhythm`).
	 * `remainingCount` — сколько пунктов ещё открыто после этого.
	 */
	onboarding_checklist_item_completed: z.object({
		itemKey: z.string(),
		remainingCount: z.number(),
	}),
	/**
	 * Чек-лист первых шагов скрыт вручную (кнопка «Скрыть», §5.5).
	 * Эмитится СЕРВЕРОМ (`onboarding.dismissChecklist`) — на клиенте не дублировать.
	 */
	onboarding_checklist_dismissed: z.object({ remainingCount: z.number() }),

	// ─── Materials ─────────────────────────────────────────────────────
	material_uploaded: z.object({
		type: z.string(),
		sizeMB: z.number(),
		setId: z.string().optional(),
	}),
	material_processed: z.object({ materialId: z.string(), durationMs: z.number() }),
	material_failed: z.object({ materialId: z.string(), errorCode: z.string() }),
	material_shared: z.object({ shareLinkId: z.string() }),

	// ─── Notes ─────────────────────────────────────────────────────────
	note_created: z.object({ source: z.enum(["manual", "ai-generated"]) }),
	note_generated: z.object({
		detailLevel: z.enum(["summarized", "in-depth", "comprehensive"]),
		durationMs: z.number(),
		tokensUsed: z.number().optional(),
	}),
	note_ai_block_used: z.object({ blockType: z.string() }),

	// ─── Flashcards ────────────────────────────────────────────────────
	flashcards_generated: z.object({
		count: z.number(),
		types: z.array(z.string()),
	}),
	flashcard_studied: z.object({
		setId: z.string(),
		rating: z.enum(["again", "hard", "good", "easy"]),
		srsState: z.string().optional(),
	}),
	flashcards_reviewed: z.object({ setId: z.string(), cardsReviewed: z.number() }),
	srs_session_completed: z.object({
		cardsReviewed: z.number(),
		accuracy: z.number(),
	}),

	// ─── Quizzes ───────────────────────────────────────────────────────
	quiz_generated: z.object({
		questionCount: z.number(),
		examType: z.string().optional(),
	}),
	quiz_started: z.object({ quizId: z.string() }),
	quiz_completed: z.object({
		quizId: z.string(),
		score: z.number(),
		timeTakenSec: z.number(),
		weakSpotsCount: z.number(),
	}),
	weak_spot_identified: z.object({ topic: z.string() }),
	weak_spot_resolved: z.object({
		topic: z.string(),
		accuracyImprovement: z.number().optional(),
	}),

	// ─── Tutor (text) ──────────────────────────────────────────────────
	tutor_session_created: z.object({
		mode: z.enum(["regular", "materials", "web-search"]),
	}),
	tutor_message_sent: z.object({
		chatMode: z.string(),
		attachmentsCount: z.number(),
		messageLength: z.number(),
	}),
	tutor_web_search_used: z.object({ queryLength: z.number() }),
	tutor_citation_clicked: z.object({ chatMode: z.string() }),
	/** Tutor Action Platform — успешный invoke tool (server telemetry). */
	tutor_tool_invoke: z.object({
		tool: z.string(),
		category: z.string(),
		sessionId: z.string().uuid(),
	}),
	/** Confirm-карточка показана пользователю (без секрета token). */
	tutor_confirm_shown: z.object({
		cardId: z.string(),
		sessionId: z.string().uuid(),
		action: z.string().optional(),
	}),
	/** Пользователь подтвердил destructive action (без секрета token). */
	tutor_confirm_ok: z.object({
		cardId: z.string(),
		sessionId: z.string().uuid(),
		action: z.string().optional(),
	}),
	/** Rich-карточка перешла в status=ready. */
	tutor_card_ready: z.object({
		kind: z.string(),
		id: z.string(),
	}),
	/** Пользователь открыл rich-карточку (deepLink / Open). */
	tutor_card_open: z.object({
		kind: z.string(),
		id: z.string(),
	}),

	// ─── Voice Tutor ───────────────────────────────────────────────────
	voice_tutor_session_started: z.object({ scope: z.string() }),
	voice_tutor_session_ended: z.object({
		durationSec: z.number(),
		costMinor: z.number().optional(),
	}),
	/** Inline voice в tutor chat / side-panel (не lesson Voice Tutor). */
	tutor_voice_inline_started: z.object({
		source: z.literal("inline"),
		sessionId: z.string().optional(),
		surface: z.enum(["main", "panel"]).optional(),
	}),
	tutor_voice_inline_ended: z.object({
		sessionId: z.string().optional(),
		reason: z.enum(["user", "ttl", "go_away", "error", "unmount", "closed"]).optional(),
		durationSec: z.number().optional(),
	}),

	// ─── Recordings ────────────────────────────────────────────────────
	recording_started: z.object({ source: z.enum(["web", "mobile"]) }),
	recording_completed: z.object({
		durationSec: z.number(),
		multiSpeaker: z.boolean(),
		speakersCount: z.number(),
	}),
	recording_lock_screen_used: z.object({
		platform: z.enum(["android", "ios"]),
	}),

	// ─── Audio Recap ───────────────────────────────────────────────────
	audio_summary_generated: z.object({
		format: z.enum(["podcast", "lecture", "audiobook", "summary"]),
		voiceMode: z.enum(["single", "multi"]),
		durationSec: z.number(),
	}),
	audio_recap_played: z.object({ recapId: z.string() }),
	audio_recap_completed: z.object({
		recapId: z.string(),
		playedDurationSec: z.number(),
	}),
	// AUDIO-RECAP-PRESETS T7: 5 событий по UX feature (style presets + custom brief + paywall).
	"audio_recap.preset_selected": z.object({
		preset: z.string(),
		format: z.enum(["podcast", "lecture", "audiobook", "summary"]),
		isDefault: z.boolean(),
	}),
	"audio_recap.custom_brief_used": z.object({
		preset: z.string().optional(),
		briefLength: z.number(),
	}),
	"audio_recap.regenerated_with_different_preset": z.object({
		previousPreset: z.string(),
		newPreset: z.string(),
		materialId: z.string(),
	}),
	"audio_recap.paywall_shown": z.object({
		entryPoint: z.enum(["route", "sidebar", "material-page"]),
	}),
	"audio_recap.paywall_cta_clicked": z.object({
		entryPoint: z.enum(["route", "sidebar", "material-page"]),
	}),

	// ─── Essays ────────────────────────────────────────────────────────
	essay_submitted: z.object({
		wordCount: z.number(),
	}),
	essay_graded: z.object({ score: z.number(), durationMs: z.number() }),

	// ─── Visual Explainer ──────────────────────────────────────────────
	visual_analysis_requested: z.object({
		category: z.enum(["formula", "chart", "handwriting", "diagram", "other"]),
	}),

	// ─── Study Plan / Calendar ─────────────────────────────────────────
	study_plan_created: z.object({
		source: z.enum(["template", "ai-generated", "manual"]),
		templateId: z.string().optional(),
	}),
	study_plan_section_completed: z.object({ sectionId: z.string() }),
	calendar_event_created: z.object({ kind: z.string() }),

	// ─── Обучающая система: Библиотека → Курс → Тема ───────────────────
	// Метрики воронки из docs/plans/LEARNING-SYSTEM-VISION.md §18.
	// ⚠ track() ТИХО дропает событие при провале Zod-валидации — значения
	// enum'ов ниже обязаны совпадать с тем, что реально передаёт вызывающий код.
	/** Курс создан. `source`: откуда пришло создание. */
	course_created: z.object({
		source: z.enum(["manual", "default", "folder", "upload"]),
	}),
	/**
	 * Состав курса изменился (добавлены/убраны материалы или папки).
	 * `added`/`removed` — количества, не идентификаторы (§14.9 PII).
	 */
	course_composition_changed: z.object({
		added: z.number(),
		removed: z.number(),
		source: z.enum(["course-page", "library", "upload", "folder"]),
	}),
	/** Пользователь открыл тему (вход в маршрут темы, §7). */
	topic_started: z.object({
		topicId: z.string(),
		courseId: z.string(),
	}),
	/**
	 * Закрыт шаг спины темы (§7): ① разобраться, ② закрепить, ③ добить пробелы.
	 * `step` — строгий union литералов, НЕ z.number() (иначе дыра в дашборде).
	 * `channel` — каким каналом человек прошёл шаг: текст или аудио (§6.6).
	 */
	topic_step_completed: z.object({
		step: z.union([z.literal(1), z.literal(2), z.literal(3)]),
		channel: z.enum(["text", "audio"]),
	}),
	/**
	 * Тема завершена. `trigger`: `explicit` — пользователь нажал «Завершить тему»
	 * (`completed_explicitly = true`), `auto` — спина закрылась сама по активностям.
	 */
	topic_completed: z.object({
		masteryPct: z.number(),
		stepsUsed: z.number(),
		trigger: z.enum(["explicit", "auto"]),
	}),
	/**
	 * Комплект темы сгенерирован. `trigger` — почему запустилась генерация:
	 * `jit` (следующая тема после завершения), `prefetch` (тема N+2),
	 * `manual` (кнопка «Подготовить тему»), `program-built` (первая тема плана).
	 * `kinds` — виды из TOPIC_KIT_KINDS, реально произведённые в этом прогоне.
	 */
	topic_kit_generated: z.object({
		trigger: z.enum(["jit", "prefetch", "manual", "program-built"]),
		kinds: z.array(z.string()),
		latencyMs: z.number(),
	}),
	/** Запрошено аудио темы (пикер формата, §6.5). */
	audio_requested: z.object({
		format: z.enum(["podcast", "lecture", "audiobook", "summary"]),
		topicId: z.string(),
	}),
	/**
	 * Аудио предзагружено в офлайн-кэш (§6.6). `reason`: `preference` —
	 * выявленное предпочтение канала, `manual` — явное «скачать».
	 * `cached` — факт наличия файла в кэше ПОСЛЕ попытки, а не постановка в очередь.
	 */
	audio_prefetched: z.object({
		reason: z.enum(["preference", "manual"]),
		cached: z.boolean(),
	}),
	/**
	 * Наведение/тап по термину глоссария — по поверхности показа (§8.1).
	 * Значения `note` / `audio-transcript` / `tutor-chat` покрывают и сцены
	 * воркспейса курса: там те же три текстовые поверхности (декрет E32),
	 * поэтому новых значений заводить не нужно — синонимы дробят дашборд.
	 */
	glossary_term_hovered: z.object({
		surface: z.enum([
			"note",
			"audio-transcript",
			"recording-transcript",
			"tutor-chat",
			"voice-transcript",
			"description",
		]),
	}),
	/** Массовое добавление материалов в курс из Библиотеки (§11). */
	library_bulk_add: z.object({ count: z.number() }),

	// ─── Course Workspace V2 (§5.9) ────────────────────────────────────
	// ⚠ Синонимы НЕ заводим. Уже существуют и переиспользуются как есть:
	//   `topic_step_completed` (step + ОБЯЗАТЕЛЬНЫЙ channel) — не дублировать;
	//   `note_created ({source:'manual'})` вместо «user_note_created»;
	//   `audio_requested ({format, topicId})` вместо «topic_audio_format_chosen»;
	//   `course_composition_changed` вместо отдельного «источник добавлен».
	/**
	 * Открыт воркспейс курса. `entry` — откуда пришли: это ключевая метрика
	 * ресерча «доходят ли до курса из дашборда / уведомления / поиска».
	 */
	course_workspace_opened: z.object({
		courseId: z.string(),
		entry: z.enum(["list", "dashboard", "deep-link", "notification", "search"]),
	}),
	/**
	 * Программа курса построена. `mode`: `generate` — AI по составу,
	 * `manual` — собрана руками в конструкторе (§5.2).
	 */
	course_program_built: z.object({
		courseId: z.string(),
		mode: z.enum(["generate", "manual"]),
		topicsCount: z.number(),
	}),
	/**
	 * Программа обновлена после изменения состава. `mode`: `soft` — дописали
	 * новые темы, пройденное не тронули; `hard` — пересобрали незавершённое.
	 */
	course_program_updated: z.object({
		courseId: z.string(),
		mode: z.enum(["soft", "hard"]),
		updatedTopics: z.number(),
	}),
	/**
	 * Выбран канал шага ① — читать или слушать (§2.1 п.2: это ВЫБОР канала
	 * одного шага, а не два шага подряд). Словарь намеренно `read|listen`
	 * (что человек выбрал), в отличие от `topic_step_completed.channel`
	 * (`text|audio` — чем шаг фактически закрыт).
	 */
	topic_channel_chosen: z.object({
		topicId: z.string(),
		channel: z.enum(["read", "listen"]),
	}),
	/**
	 * Использован инструмент из панели темы. `tool` — свободная строка:
	 * набор плиток меняется от шага к шагу и между релизами, enum пришлось бы
	 * править каждый раз (а `track()` ТИХО дропает событие при провале Zod).
	 */
	tool_used: z.object({
		tool: z.string(),
		step: z.union([z.literal(1), z.literal(2), z.literal(3)]),
	}),
	/** Создан курсовой чат репетитора (вход в курс или кнопка «Новый чат»). */
	course_chat_created: z.object({
		courseId: z.string(),
		trigger: z.enum(["ensure", "new-chat"]),
	}),
	/** Источник исключён из состава курса (материал остаётся в Библиотеке). */
	source_excluded: z.object({
		courseId: z.string(),
		itemType: z.enum(["material", "folder"]),
	}),
	/** Открыт календарь курса во встроенной сцене воркспейса (§4.6). */
	course_calendar_opened: z.object({ courseId: z.string() }),
	/**
	 * Раскрыта цепочка размышлений репетитора (§8.3). Мера того, читают ли её
	 * вообще — без события нельзя решить, оставлять ли фичу включённой.
	 */
	tutor_thoughts_expanded: z.object({
		surface: z.enum(["course-chat", "tutor-page", "side-panel"]),
		steps: z.number(),
	}),

	// ─── Петля обучения курса (ADAPTIVE-COURSE-LOOP §10) ───────────────
	// Цель → входная диагностика → тема ①②③ → контрольный тест → живой план.
	// Событий десять, и они закрывают ровно те продуктовые вопросы, на которые
	// код петли сегодня ответить не может: сколько людей вообще проходят
	// диагностику, помогает ли контрольный тест, берут ли тему-закрепление.
	//
	// ⚠ Синонимов НЕ заводим. Уже существующие переиспользуются как есть:
	//   `topic_completed` — завершение темы (пометка «без подтверждения» —
	//   ОТДЕЛЬНОЕ событие ниже, потому что это разные вопросы к данным);
	//   `topic_step_completed` — закрытие шагов ①②③;
	//   `quiz_completed` — сдача любого теста, включая контрольный.
	//
	// PII: ни текста цели, ни названий тем и разделов, ни содержимого вопросов —
	// только идентификаторы и числа (§14.9).
	/**
	 * Пользователь заявил или изменил цель курса (`study_plans.goal_*`).
	 *
	 * `place` разделяет два разных момента: `create` — цель заявлена в диалоге
	 * создания курса (там есть только свободная формулировка), `edit` — правка
	 * в параметрах курса (там же регуляторы). Одно имя на оба: вопрос к данным
	 * один — «доля курсов с заявленной целью», и делить его пополам нельзя.
	 *
	 * `hasTarget` / `hasFocus` — ФАКТ заполнения, а не текст: свободная
	 * формулировка цели — пользовательский контент, в аналитику он не уходит.
	 * `level` / `depth` необязательны — при создании курса регуляторов нет.
	 */
	course_goal_set: z.object({
		courseId: z.string(),
		place: z.enum(["create", "edit"]),
		hasTarget: z.boolean(),
		hasFocus: z.boolean(),
		level: z.string().optional(),
		depth: z.string().optional(),
	}),
	/**
	 * Заказан входной тест курса. `surface` — откуда: карточка-приглашение над
	 * сценой или тихая строка маршрута после «Позже» (вторая половина отвечает
	 * на вопрос «возвращаются ли отказавшиеся»). `retry` отделяет повтор после
	 * провала генерации от первого заказа — иначе аварии считаются как спрос.
	 */
	placement_started: z.object({
		courseId: z.string(),
		surface: z.enum(["banner", "prompt", "stage"]),
		retry: z.boolean(),
	}),
	/**
	 * Попытка входного теста обработана (серверный listener).
	 *
	 * Эмитится СЕРВЕРОМ: разметка тем рождается в Inngest, клиент о её итогах
	 * узнаёт только перекрашенными метками маршрута.
	 *
	 * `unmatchedTopics` — это ещё и сигнал качества промпта: модель назвала темы
	 * так, что они не сошлись с программой. Без него потеря половины замера
	 * выглядит как «диагностика просто ничего не нашла».
	 */
	placement_submitted: z.object({
		courseId: z.string(),
		/** Сколько тем в программе курса — знаменатель для остальных чисел. */
		programTopics: z.number(),
		/** Скольким темам проставлен замер. */
		assessedTopics: z.number(),
		/** Из них сильных (≥ `PLACEMENT_STRONG_PCT`). */
		strongTopics: z.number(),
		/** Из них слабых (< `REMEDIAL_MASTERY_PCT`). */
		weakTopics: z.number(),
		/** Названий из ответа модели, которым не нашлось темы курса. */
		unmatchedTopics: z.number(),
	}),
	/**
	 * Нажато «Позже». `stage` — на какой стадии отказались: до заказа теста
	 * (`idle`) или уже дождавшись генерации (`ready`). Это разные отказы:
	 * первый — «не хочу замеряться», второй — «передумал / долго».
	 */
	placement_skipped: z.object({
		courseId: z.string(),
		stage: z.enum(["idle", "requesting", "generating", "ready", "failed"]),
	}),
	/**
	 * Заказан контрольный тест темы. `entry` разделяет два входа: подвал
	 * подставляет тест следующим шагом маршрута, плитка сцены ③ — это
	 * добровольный выбор (решение владельца №2: второй тест подряд не
	 * навязываем). `round` — какой по счёту контрольный в этой теме.
	 */
	checkpoint_requested: z.object({
		topicId: z.string(),
		entry: z.enum(["footer", "gap-tile"]),
		round: z.number(),
		hasGaps: z.boolean(),
		/** Результат теста шага ②, из-за которого контрольный и понадобился. */
		stepTwoScorePct: z.number().optional(),
	}),
	/**
	 * Контрольный сдан (≥ `CHECKPOINT_PASS_PCT`). Пара к `checkpoint_failed`:
	 * два имени, а не одно с флагом, потому что вопрос «сколько тем
	 * подтверждается замером» и вопрос «сколько раундов добора нужно» —
	 * разные воронки, и склеивать их пришлось бы разбивкой в каждом отчёте.
	 */
	checkpoint_passed: z.object({
		topicId: z.string(),
		scorePct: z.number(),
		round: z.number(),
	}),
	/** Контрольный не сдан. `gapsCount` — видит ли человек, что именно добирать. */
	checkpoint_failed: z.object({
		topicId: z.string(),
		scorePct: z.number(),
		round: z.number(),
		gapsCount: z.number(),
	}),
	/**
	 * Шаг маршрута закрыт БЕЗ проверки: человек нажал «Пропустить» и подтвердил
	 * (решение владельца 2026-08-18 — карточки, тест шага ②, контрольный).
	 * Отдельное событие, а не поле `topic_step_completed`: замера не было, и
	 * доля таких тем обязана считаться отдельно, иначе она растворится в общей
	 * «пройдено».
	 *
	 * Имя намеренно без слова `quiz`: событие принадлежит МАРШРУТУ ТЕМЫ
	 * (`courses`), а не модулю тестов, и попадать в его аналитику не должно —
	 * иначе гейт покрытия (`module-registry-r12-analytics`) зазеленел бы модуль
	 * `quizzes`, у которого своей аналитики по-прежнему нет.
	 */
	topic_check_skipped: z.object({
		topicId: z.string(),
		/** Что именно пропущено: карточки, тест шага ② или контрольный. */
		target: z.enum(["flashcards", "quiz", "checkpoint"]),
	}),
	/**
	 * Тема завершена БЕЗ подтверждения замером (гейт мягкий — решение владельца
	 * №1). Отдельное событие, а не поле у `topic_completed`: последнее меряет
	 * прохождение курса, это — цену мягкого гейта, и смешивать их в одном имени
	 * значит потерять обе метрики.
	 *
	 * `hadStepTwoAttempt` отделяет «тест шага ② был, но результат слабый» от
	 * «замера не было вовсе» (в теме может не быть теста).
	 */
	topic_completed_unverified: z.object({
		topicId: z.string(),
		hadStepTwoAttempt: z.boolean(),
		/** Контрольный подставлялся следующим шагом маршрута. */
		checkpointRequired: z.boolean(),
		/** Контрольный существует и был сдан хотя бы раз (пусть и ниже порога). */
		checkpointAttempted: z.boolean(),
	}),
	/**
	 * Принято предложение темы-закрепления по итогам раздела (решение владельца
	 * №5: предложение, а не молчаливая перекройка плана). Считаем ПРИНЯТИЕ, а не
	 * показ карточки: показ виден по `topic_completed` последней темы раздела.
	 */
	remedial_topic_added: z.object({
		courseId: z.string(),
		/** Сколько слабых тем раздела стало поводом для предложения. */
		weakTopics: z.number(),
	}),
	/**
	 * Открыт репетитор из блока «Обсудить с репетитором». `intent` — откуда:
	 * из-под конспекта темы (`topic`) или с экрана проваленного контрольного
	 * (`mistakes`). `entry` отделяет большую кнопку от готовой заготовки —
	 * без него не видно, нужны ли заготовки вообще.
	 */
	note_discuss_opened: z.object({
		intent: z.enum(["topic", "mistakes"]),
		entry: z.enum(["cta", "chip"]),
		/** Какая именно заготовка, если жали по ней. */
		chip: z.enum(["simpler", "examples", "mistakes"]).optional(),
		gapsCount: z.number(),
	}),

	// ─── Инструменты курса через чат (решения U1/U5/U6) ────────────────
	// Клик по плитке покрывает существующее `tool_used` — дубля не заводим.
	/**
	 * Сабмит попапа инструмента: пользователь подтвердил параметры и запросил
	 * артефакт. Меряет, доходят ли до кнопки «Сгенерировать» (сколько попапов
	 * открыли — видно по `tool_used`).
	 */
	chat_artifact_requested: z.object({
		kind: z.enum(["diagram", "infographic"]),
		/** diagram: тип mermaid; infographic: id стиля. */
		style: z.string().optional(),
		orientation: z.enum(["landscape", "portrait", "square"]).optional(),
		detail: z.enum(["brief", "standard", "detailed"]).optional(),
	}),
	/** Артефакт реально появился (`status='ready'`). Пара к `_requested`. */
	chat_artifact_created: z.object({
		kind: z.enum(["diagram", "infographic"]),
		subkind: z.string().optional(),
		durationMs: z.number().optional(),
	}),
	/** Терминальный провал. Без него воронка «запросили → получили» слепа. */
	chat_artifact_failed: z.object({
		kind: z.enum(["diagram", "infographic"]),
		reason: z.string(),
	}),
	/**
	 * U6: ответ репетитора сохранён в артефакты. `alreadyExisted` отделяет
	 * повторный клик (идемпотентность) от реального сохранения.
	 */
	tutor_answer_saved: z.object({
		hasCourse: z.boolean(),
		alreadyExisted: z.boolean(),
	}),
	/**
	 * U6: сохранённый ответ превращён в материал-источник курса.
	 * `attached=false` — материал создан, но в состав курса не попал
	 * (громкая деградация, а не тихий успех).
	 */
	saved_answer_converted: z.object({ attached: z.boolean() }),

	// ─── Воронка создания курса (COURSE-CREATION-FLOW §8) ──────────────
	// Путь «Создать курс» → развилка трёх сценариев → бриф → черновик →
	// диагностика → корректировка → утверждение. Кода под ним много, а измерять
	// было нечем: ни одного события. Без этих семи непроверяемы главные вопросы
	// флоу — какой сценарий берут, где отваливаются в брифе, доходят ли до
	// утверждения и покрывает ли каталог спрос.
	//
	// PII: ни темы курса, ни цели, ни текста запроса в каталог, ни названий тем —
	// только идентификаторы курса, перечислимые значения и числа (§14.9).
	//
	// ⚠ Синонимов НЕ заводим. Переиспользуются как есть:
	//   `course_goal_set ({place:'create'})` — цель, заявленная в брифе;
	//   `course_template_enrolled` — запись из подборщика (он зовёт ту же
	//   мутацию, что и карточка каталога);
	//   `placement_started` / `placement_skipped` — диагностика черновика это та
	//   же диагностика курса.
	/**
	 * Выбрана плитка развилки (§4.1). `path` — какой из трёх сценариев взяли,
	 * `entry` — с какой поверхности пришли в развилку.
	 *
	 * `entry` — не косметика: развилка открывается с шести мест, и вопрос
	 * «работает ли глобальный „+“ как вход в главную сущность продукта» (§4.0)
	 * без разбивки по поверхности не отвечается вовсе. Одно событие с двумя
	 * полями, а не три имени по числу плиток: воронка «открыл развилку → выбрал»
	 * обязана считаться одним знаменателем.
	 */
	course_create_path_chosen: z.object({
		path: z.enum(["topic", "catalog", "empty"]),
		entry: z.enum([
			"courses-list",
			"add-menu",
			"dashboard",
			"checklist",
			"onboarding",
			"how-it-works",
		]),
	}),
	/**
	 * Пройден шаг брифа (§4.2) — то, чем меряется «где отваливаются».
	 *
	 * `step` — порядковый номер (1-based), знаменатель воронки; `stepName` — имя
	 * того же шага. Номер переживёт перестановку, имя — вставку шага; вместе они
	 * читаются без сверки с релизом.
	 *
	 * `filled` — дал ли человек содержательный ответ на вопрос шага, а не факт
	 * показа: `goal` — цель непуста, `level` — назван конкретный уровень (а не
	 * «не знаю — проверьте тестом»), `materials` — приложен хотя бы один файл.
	 * У обязательного `topic` и у финального `review` всегда `true`. Текстов при
	 * этом нет ни одного: тема и цель — пользовательский контент.
	 *
	 * Шаг `review` считается пройденным по УСПЕХУ `courses.createFromBrief`:
	 * бриф закончился тем, ради чего он был, — курсом-черновиком.
	 */
	brief_step_completed: z.object({
		step: z.number(),
		stepName: z.enum(["topic", "goal", "level", "materials", "review"]),
		filled: z.boolean(),
	}),
	/**
	 * Черновик программы собран (§5.2). Эмитится СЕРВЕРОМ (`generateStudyPlan`,
	 * ветка `draft`): сборка идёт около минуты после редиректа, и клиентский эмит
	 * терял бы ровно те случаи, ради которых событие заводится, — когда человек
	 * не дождался и ушёл.
	 *
	 * НЕ синоним `course_program_built`: то событие про программу ЛЮБОГО курса и
	 * шлётся клиентом со сцены «шаг 0»; это — про исход брифа, и в нём есть то,
	 * чего клиент не знает (число источников, ветка генератора, повтор).
	 *
	 * `sourcesCount` — «были ли источники» числом: черновик по одной теме без
	 * единого файла — законный путь (§0), и доля таких курсов есть главная
	 * проверка гипотезы «материалы не турникет».
	 *
	 * `rebuild` отделяет первую сборку от «Пересобрать» на экране черновика: без
	 * него повторы завышают ступень воронки.
	 */
	draft_built: z.object({
		courseId: z.string(),
		topicsCount: z.number(),
		sourcesCount: z.number(),
		source: z.enum(["topic", "materials", "collection"]),
		rebuild: z.boolean(),
	}),
	/**
	 * Нажато «Утвердить программу» (§4.4) — последняя ступень воронки создания и
	 * вход в петлю обучения.
	 *
	 * `placement` — чем закончилась диагностика К МОМЕНТУ утверждения (`none` —
	 * её не трогали вовсе). Это ответ на вопрос, ради которого диагностику и
	 * поставили в черновик: утверждают программу после замера или мимо него.
	 */
	draft_confirmed: z.object({
		courseId: z.string(),
		topicsCount: z.number(),
		placement: z.enum(["done", "skipped", "none"]),
	}),
	/**
	 * Применена корректировка черновика по итогам диагностики (§5.4). Считаем
	 * ПРИМЕНЕНИЕ, а не показ карточки: показ выводится из `placement_submitted`
	 * в черновике.
	 *
	 * `proposed` / `applied` — предложено и создано: разница между ними и есть
	 * мера доверия к предложению (сняли галочки — значит предложили лишнее).
	 * `complete: false` — часть тем не создалась (частичное применение не
	 * откатывается, §4.4), то есть событие не выдаёт провал за успех.
	 */
	draft_adjusted: z.object({
		courseId: z.string(),
		proposed: z.number(),
		applied: z.number(),
		complete: z.boolean(),
	}),
	/**
	 * Подборщик каталога что-то нашёл (§4.3). Пара к `finder_miss`: вместе они
	 * отвечают, покрывает ли каталог спрос, — по одной половине это не видно.
	 *
	 * `source` — из плитки развилки или из строки совпадений брифа: это разный
	 * спрос («хочу готовое» против «собираю своё, но посмотрю»), и склеивать их
	 * конверсии бессмысленно.
	 *
	 * `queryLength`, а не сам запрос: текст поиска — пользовательский контент.
	 * `officialCount` отделяет программы платформы от авторских курсов — пустой
	 * каталог авторов виден только так.
	 */
	finder_hit: z.object({
		source: z.enum(["path-dialog", "brief"]),
		queryLength: z.number(),
		resultsCount: z.number(),
		officialCount: z.number(),
	}),
	/** Подборщик не нашёл ничего. Только длина запроса — текст не логируем. */
	finder_miss: z.object({
		source: z.enum(["path-dialog", "brief"]),
		queryLength: z.number(),
	}),

	// ─── Шаблоны курсов (§5.8) ─────────────────────────────────────────
	// Воронка каталога целиком: выпустил → показали → записался → обновился →
	// форкнул. Без неё продуктовые гипотезы каталога непроверяемы (§14 п.16).
	// Синонимов не заводим: «копия курса» покрыта общим путём `courses.duplicate`,
	// и отдельного `course_duplicated` в каталоге нет — форк шаблона отличается
	// от копии своего курса только тем, что источник чужой, поэтому событие ОДНО
	// и эмитится ровно там, где эта разница известна.
	/**
	 * Автор выпустил версию шаблона (`courseTemplates.publish`). `version` —
	 * номер снимка, `isFirstPublish` отделяет вход в каталог от перевыпуска N+1.
	 */
	course_template_published: z.object({
		templateId: z.string(),
		version: z.number(),
		isFirstPublish: z.boolean(),
		topicsCount: z.number(),
	}),
	/**
	 * Автопроверка публикации вынесла вердикт (`reviewCoursePublication`).
	 *
	 * Ключевая метрика гейта — доля `approved` без участия человека: если она
	 * низкая, очередь модерации становится узким горлом выхода в каталог, и это
	 * видно только здесь. `decidedBy` отделяет решение модели от стоп-листа и от
	 * деградации (провайдер недоступен), иначе рост `manual` невозможно
	 * объяснить: то ли авторы стали хуже, то ли у нас отвалился классификатор.
	 *
	 * `anonymous` — потому что анонимная публикация не несёт репутационных
	 * издержек, и разница в доле нарушений между подписями — прямой аргумент за
	 * или против ужесточения порога.
	 */
	course_template_reviewed: z.object({
		templateId: z.string(),
		status: z.enum(["approved", "rejected", "manual"]),
		verdict: z.enum(["allow", "review", "reject"]),
		decidedBy: z.enum(["ai", "detector", "human", "degraded"]),
		findings: z.number(),
		anonymous: z.boolean(),
		trigger: z.enum(["publish", "backfill", "listing-edit"]),
	}),
	/**
	 * Карточка снята с публикации либо возвращена в каталог (`setStatus`).
	 * Это НЕ синоним `course_template_published`: там выпуск СНИМКА, здесь —
	 * видимость карточки. Оба направления одним именем: два имени поделили бы
	 * воронку пополам, а вопрос к данным один — «сколько авторов закрывают приём».
	 */
	course_template_status_changed: z.object({
		templateId: z.string(),
		status: z.enum(["published", "unpublished"]),
	}),
	/**
	 * Ученик записался на шаблон. `alreadyEnrolled` отделяет повторный клик
	 * (мутация идемпотентна) от реальной записи — иначе конверсия каталога
	 * завышена на все двойные тапы.
	 */
	course_template_enrolled: z.object({
		templateId: z.string(),
		version: z.number(),
		alreadyEnrolled: z.boolean(),
	}),
	/**
	 * Ученик обновил свой курс до новой версии шаблона (§5.8.4). Счётчики тем —
	 * мера того, насколько болезненно обновление: `rebuiltTopics` = сколько
	 * прогресса реально сбросилось.
	 */
	course_template_updated: z.object({
		templateId: z.string(),
		fromVersion: z.number(),
		toVersion: z.number(),
		rebuiltTopics: z.number(),
		addedTopics: z.number(),
		removedTopics: z.number(),
	}),
	/**
	 * Форк шаблонного курса (§5.8.5) — «взял чужой курс за основу, дополню
	 * своим». Источники автора не переносятся никогда, поэтому в props их нет:
	 * значение было бы константой.
	 */
	course_template_forked: z.object({
		templateId: z.string(),
		includeProgram: z.boolean(),
		includeArtifacts: z.boolean(),
	}),

	// ─── Обложки и локализация курсов ──────────────────────────────────
	/**
	 * Обложка нарисована. `reason` разделяет автоматику и ручной повтор: у них
	 * разная цена (ручная списывает квоту) и разный смысл — доля `manual`
	 * показывает, насколько плохо автоматика угадывает с первого раза.
	 */
	course_cover_generated: z.object({
		courseId: z.string(),
		reason: z.enum(["program-built", "manual"]),
	}),
	/** Автор заменил обложку своей картинкой — сигнал качества генерации. */
	course_cover_replaced: z.object({ courseId: z.string() }),
	/**
	 * Ученик перевёл курс на свой язык. `scope` отделяет каркас (разово на курс)
	 * от комплекта темы (по мере чтения): одна метрика смешивала бы решение
	 * «хочу этот курс по-русски» с фактом чтения N-й темы.
	 */
	course_translated: z.object({
		courseId: z.string(),
		fromLocale: z.string(),
		toLocale: z.string(),
		scope: z.enum(["skeleton", "topic"]),
	}),
	/**
	 * Ученик выбрал читать в оригинале. Пара к `course_translated`: без неё видно
	 * только согласия, и предложение перевода выглядит безупречным.
	 */
	course_translation_declined: z.object({
		courseId: z.string(),
		courseLocale: z.string(),
		userLocale: z.string(),
	}),
	/** Метаданные шаблона разлиты по локалям при публикации (для поиска в каталоге). */
	course_template_metadata_translated: z.object({
		templateId: z.string(),
		localesCount: z.number(),
	}),

	// ─── Публичная витрина курсов (`/catalog`, `/catalog/{slug}`) ──────
	// Воронка ровно из трёх ступеней: увидел карточку → нажал «Начать» →
	// упёрся в оплату. Считать её по `course_template_enrolled` нельзя: то
	// событие знает только про УСПЕШНЫЕ записи и живёт внутри сессии, а
	// витрину открывает в том числе гость, у которого сессии нет вовсе.
	/**
	 * Открыта публичная карточка курса. `authenticated` — знаменатель для
	 * обеих половин воронки сразу: гостю показывают вход, а не запись, и
	 * смешивать их конверсии бессмысленно.
	 *
	 * `slug`, а не `templateId`: адрес и есть то, чем страница различима в
	 * выдаче поисковика, и по нему же сходятся данные с внешней аналитикой.
	 */
	course_public_page_viewed: z.object({
		slug: z.string(),
		origin: z.enum(["author", "official"]),
		pricingKind: z.enum(["free", "paid"]),
		authenticated: z.boolean(),
	}),
	/**
	 * Нажато главное действие витрины. `intent` — та самая ветка, которую
	 * выбрал `resolveStartIntent`: клик по одной кнопке ведёт в пять разных
	 * мест, и без разбивки метрика склеивает «пошёл учиться» с «его отправили
	 * логиниться».
	 */
	course_start_clicked: z.object({
		slug: z.string(),
		// `import` — перенос готовой программы платформы, `preset` — генерация
		// пресета без структуры. Разводить их обязательно: до 2026-08-16 обе
		// ветки шли генерацией, и метрика не показывала, что курсы с написанной
		// программой пересобирались моделью вместо переноса.
		intent: z.enum(["login", "import", "preset", "open-course", "purchase", "configure"]),
	}),
	/**
	 * Запись не состоялась из-за оплаты. Два повода разведены намеренно:
	 * `PAYMENT_REQUIRED` — нормальный платный курс (это спрос), а
	 * `PAYMENT_UNAVAILABLE` — погашённый killswitch платежей, то есть НАША
	 * авария. Одно имя на оба означало бы, что всплеск отказов из-за
	 * поломки читается как рост интереса к платным курсам.
	 */
	course_enroll_blocked_payment: z.object({
		templateId: z.string(),
		reason: z.enum(["PAYMENT_REQUIRED", "PAYMENT_UNAVAILABLE"]),
	}),
	/**
	 * Описание карточки сгенерировано. `force` отделяет первую генерацию от
	 * ручного перевыпуска автором, `outcomes` — грубая мера полноты текста
	 * (пустой список означает карточку без «чему научитесь»).
	 */
	course_listing_generated: z.object({
		origin: z.enum(["author", "official"]),
		outcomes: z.number(),
		force: z.boolean(),
	}),

	// ─── Streaks / Progress / Recommendations ──────────────────────────
	streak_continued: z.object({ currentStreak: z.number() }),
	streak_broken: z.object({ previousStreak: z.number() }),
	streak_freeze_used: z.object({ currentStreak: z.number() }),
	recommendation_clicked: z.object({ kind: z.string(), reason: z.string() }),

	// ─── Billing ───────────────────────────────────────────────────────
	pricing_viewed: z.object({ fromPage: z.string() }),
	checkout_started: z.object({
		tier: z.enum(["plus", "pro"]),
		provider: z.enum(["yookassa", "paypal"]),
		billingCycle: z.enum(["monthly", "annual"]),
	}),
	trial_started: z.object({ tier: z.string() }),
	subscription_created: z.object({
		tier: z.string(),
		currency: z.string(),
		amount: z.number(),
	}),
	subscription_upgraded: z.object({
		fromTier: z.string(),
		toTier: z.string(),
		billingCycle: z.string(),
	}),
	subscription_canceled: z.object({
		tier: z.string(),
		reason: z.string().optional(),
	}),
	payment_succeeded: z.object({
		provider: z.enum(["yookassa", "paypal"]),
		amountMinor: z.number(),
		currency: z.string(),
	}),
	refund_processed: z.object({ amountMinor: z.number(), currency: z.string() }),
	quota_exceeded: z.object({ useCase: z.string(), tier: z.string() }),

	// ─── Search ────────────────────────────────────────────────────────
	search_performed: z.object({ scope: z.string(), resultsCount: z.number() }),
	share_link_created: z.object({ entityType: z.string() }),
	share_link_opened: z.object({ entityType: z.string() }),

	// ─── Admin ─────────────────────────────────────────────────────────
	"admin.user.banned": z.object({ targetUserId: z.string() }),
	"admin.impersonation.started": z.object({ targetUserId: z.string() }),

	// ─── Errors / Generic ──────────────────────────────────────────────
	error_displayed: z.object({ code: z.string(), module: z.string() }),
	crash_recovered: z.object({ module: z.string() }),

	// ─── Graceful degradation (rules/logging.md §13a) ──────────────────
	// Polling-fallback таб-панели материала выявил терминальное состояние
	// артефакта (ready/failed) БЕЗ предшествующего Socket.IO события — значит
	// real-time канал не доставил `*:ready`/`*:generation_failed`. Маркер
	// эмитится один раз на генерацию (не на каждый poll), чтобы было видно,
	// как часто real-time degrade'ится в polling. `artifact` — note/flashcards/
	// quiz; `terminal` — что именно поймал polling.
	"artifact_generation.poll_resolved": z.object({
		artifact: z.enum(["flashcards", "quiz"]),
		terminal: z.enum(["ready", "failed"]),
	}),
	/**
	 * Real-time канал ушёл на ухудшенный путь или отвалился совсем.
	 *
	 * Заведено после инцидента, в котором Socket.IO не подключался вообще, а
	 * узнать об этом можно было ровно одним способом — открыть DevTools у
	 * пользователя: клиент молча ретраил websocket по кругу, сервер о неудачном
	 * подключении не знает по определению (до него не доходит), и ни одного
	 * следа наружу не выходило.
	 *
	 * `kind`: `polling-fallback` — соединение живо, но на медленном транспорте;
	 * `connect-failed` — соединения нет, real-time не работает. Эмитится ОДИН
	 * раз на эпизод (сброс — на успешном `connect`), иначе бесконечный reconnect
	 * зальёт поток тысячами одинаковых событий.
	 */
	realtime_degraded: z.object({
		kind: z.enum(["polling-fallback", "connect-failed"]),
		reason: z.string(),
	}),

	// ─── Внешний модуль `characters` (федерация с CharacterHub, Ш7) ────
	// Воронка ровно из двух половин: связывание (сколько доходит от баннера до
	// рабочего модуля) и использование (сколько сообщений и сколько из них с
	// цитатами). Вторая половина важнее первой: сессия без цитат означает, что
	// модель ушла в болтовню мимо материала, и учебной ценности в ней нет
	// (контрольная метрика §19 федерации).
	/** Нажали «Начать» на экране входа. Знаменатель воронки связывания. */
	character_link_started: z.object({}),
	/** Аккаунт связан. `existing` отделяет первый вход от повторного устройства. */
	character_link_completed: z.object({ existing: z.boolean() }),
	/** Связывание не удалось. `reason` — блокер входа либо класс отказа партнёра. */
	character_link_failed: z.object({ reason: z.string() }),
	character_session_created: z.object({
		scenario: z.string(),
		/** Откуда вошли: сам раздел или шаг ③ темы курса. */
		scope: z.enum(["standalone", "topic"]),
		materialsCount: z.number(),
	}),
	/**
	 * Ход завершён. `hasCitations` — та самая контрольная метрика: без неё
	 * невозможно отличить учебный формат от развлекательного.
	 */
	character_message_sent: z.object({
		scenario: z.string(),
		hasCitations: z.boolean(),
	}),
	/** Ушли в Hero по одноразовой ссылке. */
	character_deeplink_opened: z.object({ target: z.string() }),
	/** Партнёр деградировал на глазах пользователя (не тихо — см. rules/logging §13a). */
	character_external_degraded: z.object({ reason: z.string() }),
	/** Сработала НАША модерация (не чужой clamp): направление + категория. */
	character_moderation_flag: z.object({
		direction: z.enum(["outbound", "inbound"]),
		category: z.string(),
	}),

	// ─── Aggregated AI ─────────────────────────────────────────────────
	ai_generation_started: z.object({
		useCase: z.string(),
		sourceMaterialId: z.string().optional(),
	}),
	ai_generation_completed: z.object({
		useCase: z.string(),
		durationMs: z.number(),
		tokensUsed: z.number().optional(),
		success: z.boolean(),
	}),
} as const;

export type EventName = keyof typeof events;
export type EventProps<N extends EventName> = z.infer<(typeof events)[N]>;

/** Все имена событий — для admin-UI/документации/runtime-валидации. */
export const EVENT_NAMES = Object.keys(events) as EventName[];

/** true, если строка — валидное имя события из каталога. */
export function isKnownEvent(name: string): name is EventName {
	return name in events;
}
